import { Webcast, WebcastPlayer } from '@graphql/gql/graphql';
import { SessionStatus, isAfterSessionStatus } from '@tools/sessionUtils/SessionStatus';
import isVendorEmbedded, { Vendor, SolutionType, WebcastFormat, WebcastType } from '@tools/sessionUtils/Vendor';

type SessionStatusType = keyof typeof SessionStatus;

export const returnWebcastPlayer = (webcast: Webcast): Partial<WebcastPlayer> => webcast?.player || {};

export const isOnDemandVideo = (sessionStatus: SessionStatusType, webcast: Webcast): boolean =>
  isAfterSessionStatus(sessionStatus) && webcast?.onDemandVideo;

export const checkIsVirtualRoomEmbedded = ({
  isVirtual,
  sessionStatus,
  vendor,
  isZoomEmbedEnabled = false // TODO WAX-2383 remove isZoomEnabled post-release
}: {
  isVirtual: boolean;
  vendor: string;
  sessionStatus: SessionStatusType;
  isZoomEmbedEnabled?: boolean;
}): boolean => isVirtual && vendor && isVendorEmbedded(vendor, sessionStatus, isZoomEmbedEnabled);

export const checkIsBrightcoveStream = (vendor: string): boolean =>
  vendor === Vendor.BRIGHTCOVE || vendor === Vendor.CVP;

export const checkIsWebcastCVCCollaborative = (
  webcastFormat: string,
  webcastType: string,
  solutionType: string
): boolean =>
  webcastFormat === WebcastFormat.LIVE && webcastType === WebcastType.MEETING && solutionType === SolutionType.CVC;

export const checkHasRecordingInProgress = ({
  hasOnDemandVideo,
  vendor,
  webcastVideoId,
  streamRecordingStatus
}: {
  hasOnDemandVideo: boolean;
  vendor: string;
  webcastVideoId: string;
  streamRecordingStatus: string;
}): boolean =>
  hasOnDemandVideo && vendor === Vendor.CVP && !webcastVideoId && streamRecordingStatus === 'creating_asset';

export const checkIsEmbeddedCollaboration = (vendor: string): boolean => vendor === Vendor.EMBEDDED_CVC;

export const checkIsContentPresent = ({
  isStudio,
  isEmbeddedCollab,
  hasOnDemandVideo,
  hasRecordingInProgress,
  isCVCCollaborative,
  isLive,
  showVirtualRoom = false,
  joinEmbedRole,
  statusOnLoad,
  videoContent
}: {
  isStudio: boolean;
  isEmbeddedCollab: boolean;
  hasOnDemandVideo: boolean;
  hasRecordingInProgress: boolean;
  isCVCCollaborative: boolean;
  isLive: boolean;
  showVirtualRoom?: boolean;
  joinEmbedRole: string;
  statusOnLoad: SessionStatusType;
  videoContent: string;
}): boolean =>
  (statusOnLoad !== SessionStatus.AFTER && (!!videoContent || isEmbeddedCollab || isStudio)) ||
  (statusOnLoad === SessionStatus.AFTER &&
    (hasOnDemandVideo || isLive || showVirtualRoom || isStudio) &&
    (!!videoContent || (isCVCCollaborative && hasRecordingInProgress) || (isEmbeddedCollab && joinEmbedRole != null)));

export const checkIsWebcastEmbeddedCVCCollaborative = (
  webcastFormat: string,
  webcastType: string,
  solutionType: string
): boolean =>
  webcastFormat === WebcastFormat.LIVE &&
  webcastType === WebcastType.MEETING &&
  solutionType === SolutionType.EMBEDDED_CVC;
